import { Type } from 'class-transformer';
import {
	ArrayMaxSize,
	IsArray,
	IsBoolean,
	IsEnum,
	IsIn,
	IsInt,
	IsNumber,
	IsOptional,
	IsString,
	IsUUID,
	Max,
	MaxLength,
	Min,
	MinLength,
	Validate,
	ValidateNested,
	ValidatorConstraint,
	ValidatorConstraintInterface,
} from 'class-validator';
import { CourseSortKey, CoursesCourseStatus } from '../../../enums/courses.enum';

export const COURSES_LESSON_POSITION_KINDS = ['pdf', 'epub', 'article'] as const;
export type CoursesLessonPositionKind = (typeof COURSES_LESSON_POSITION_KINDS)[number];
export const COURSES_LESSON_POSITION_ZOOM_MODES = ['fit-width', 'fit-height'] as const;

export const COURSES_QUIZ_MAX_ANSWER_TEXT = 5000;
export const COURSES_QUIZ_MAX_ANSWERS_PER_ATTEMPT = 200;
export const COURSES_QUIZ_MAX_OPTIONS_PER_ANSWER = 50;
export const COURSES_NOTE_MAX_LENGTH = 10000;
export const COURSES_JOURNAL_MAX_LENGTH = 20000;

@ValidatorConstraint({ name: 'coursesLessonPositionZoom', async: false })
export class CoursesLessonPositionZoomConstraint implements ValidatorConstraintInterface {
	validate(value: unknown): boolean {
		if (typeof value === 'number') {
			return Number.isFinite(value) && value >= 10 && value <= 1000;
		}

		return (
			typeof value === 'string' &&
			(COURSES_LESSON_POSITION_ZOOM_MODES as readonly string[]).includes(value)
		);
	}

	defaultMessage(): string {
		return 'zoom must be a number between 10 and 1000, "fit-width" or "fit-height"';
	}
}

export const COURSES_CATALOG_LIST_STATUSES = [
	CoursesCourseStatus.Published,
	CoursesCourseStatus.Completed,
] as const;

export class CoursesCatalogListQueryDto {
	@IsOptional()
	@IsIn(COURSES_CATALOG_LIST_STATUSES)
	status?: CoursesCourseStatus.Published | CoursesCourseStatus.Completed;

	@IsOptional()
	@IsEnum(CourseSortKey)
	sort?: CourseSortKey;
}

export class CoursesOverallLeaderboardQueryDto {
	@IsOptional()
	@IsUUID()
	courseId?: string;
}

export class CoursesMarkLessonCompleteDto {
	/** Defaults to `true` in the service. */
	@IsOptional()
	@IsBoolean()
	completed?: boolean;
}

export class CoursesLessonPositionDto {
	@IsIn(COURSES_LESSON_POSITION_KINDS)
	kind: CoursesLessonPositionKind;

	@IsOptional()
	@IsInt()
	@Min(1)
	@Max(100000)
	page?: number;

	@IsOptional()
	@Validate(CoursesLessonPositionZoomConstraint)
	zoom?: number | 'fit-width' | 'fit-height';

	@IsOptional()
	@IsString()
	@MaxLength(2000)
	cfi?: string;

	@IsOptional()
	@IsNumber()
	@Min(0)
	@Max(1)
	scroll?: number;
}

export class CoursesSaveLessonPositionDto {
	@ValidateNested()
	@Type(() => CoursesLessonPositionDto)
	position: CoursesLessonPositionDto;
}

export class CoursesQuizAnswerDto {
	@IsUUID()
	question_id: string;

	@IsOptional()
	@IsArray()
	@ArrayMaxSize(COURSES_QUIZ_MAX_OPTIONS_PER_ANSWER)
	@IsUUID(undefined, { each: true })
	selected_option_ids?: string[];

	@IsOptional()
	@IsString()
	@MaxLength(COURSES_QUIZ_MAX_ANSWER_TEXT)
	answer_text?: string;
}

export class CoursesSubmitQuizDto {
	@IsArray()
	@ArrayMaxSize(COURSES_QUIZ_MAX_ANSWERS_PER_ATTEMPT)
	@ValidateNested({ each: true })
	@Type(() => CoursesQuizAnswerDto)
	answers: CoursesQuizAnswerDto[];
}

export class CoursesSubmitJournalEntryDto {
	@IsString()
	@MinLength(1)
	@MaxLength(COURSES_JOURNAL_MAX_LENGTH)
	content: string;
}

export class CoursesCreateNoteDto {
	@IsString()
	@MinLength(1)
	@MaxLength(COURSES_NOTE_MAX_LENGTH)
	content: string;
}

export class CoursesUpdateNoteDto {
	@IsString()
	@MinLength(1)
	@MaxLength(COURSES_NOTE_MAX_LENGTH)
	content: string;
}

export const COURSES_INVALID_LESSON_ROUTE_OUTCOMES = [
	'notfound_missing_params',
	'notfound_invalid_params',
	'notfound_missing_lesson',
	'redirect_module_mismatch',
] as const;

export class CoursesLogInvalidLessonRouteDto {
	@IsIn(COURSES_INVALID_LESSON_ROUTE_OUTCOMES)
	outcome: (typeof COURSES_INVALID_LESSON_ROUTE_OUTCOMES)[number];

	@IsOptional()
	@IsString()
	@MaxLength(200)
	matchedRoute?: string;

	@IsOptional()
	@IsString()
	@MaxLength(200)
	requestedModuleId?: string;

	@IsOptional()
	@IsString()
	@MaxLength(200)
	requestedLessonId?: string;

	@IsOptional()
	@IsString()
	@MaxLength(200)
	resolvedModuleId?: string;

	@IsOptional()
	@IsString()
	@MaxLength(200)
	resolvedLessonId?: string;
}

export const COURSES_PDF_LOAD_FAILURE_STAGES = [
	'preflight_http_error',
	'preflight_network_error',
	'iframe_render_error',
	'canvas_load_error',
	'recovery_failed',
] as const;

export class CoursesLogPdfLoadFailureDto {
	@IsIn(COURSES_PDF_LOAD_FAILURE_STAGES)
	stage: (typeof COURSES_PDF_LOAD_FAILURE_STAGES)[number];

	@IsOptional()
	@IsString()
	@MaxLength(200)
	lessonId?: string;

	@IsOptional()
	@IsInt()
	@Min(0)
	@Max(599)
	status?: number;

	@IsOptional()
	@IsString()
	@MaxLength(500)
	message?: string;

	@IsOptional()
	@IsBoolean()
	recovered?: boolean;

	@IsOptional()
	@IsString()
	@MaxLength(200)
	urlHost?: string;
}
